import { ChartData, ChartOptions, Plugin } from 'chart.js';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';

interface Props {
    totalRegistrations: {
        name: string;
        count: number;
        percentage: number;
    }[];
}

const GuestRegistrationAnalytic = ({ totalRegistrations }: Props) => {
    const hasData = totalRegistrations?.some((status) => status.count > 0);

    const options: ChartOptions<'doughnut'> = useMemo(
        () => ({
            cutout: '70%',
            rotation: 230,
            circumference: 260,
            radius: 100,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (tooltipItem) => {
                            const status = totalRegistrations.find(
                                (s) => s.name.toLowerCase() === tooltipItem.label.toLowerCase()
                            );
                            return status?.count.toString();
                        },
                    },
                },
            },
            animation: {
                duration: 500,
            },
            aspectRatio: 1.5,
        }),
        [totalRegistrations]
    );

    const placeholderOptions: ChartOptions<'doughnut'> = useMemo(
        () => ({
            ...options,
            cutout: '90%',
            radius: 90,
        }),
        [options]
    );

    const placeholderData: ChartData<'doughnut'> = useMemo(
        () => ({
            label: [''],
            datasets: [
                {
                    data: [1],
                    backgroundColor: '#E9EDF0',
                    borderWidth: 0,
                    borderRadius: 50,
                },
            ],
        }),
        []
    );

    const data: ChartData<'doughnut'> = useMemo(
        () => ({
            labels: totalRegistrations.map((x) => x.name),
            datasets: [
                {
                    data: totalRegistrations.map((value) => value.percentage),
                    backgroundColor: ['#005FBE', 'transparent'],
                    borderWidth: 0,
                    borderRadius: 50,
                    hoverBackgroundColor: ['#005FBE', 'transparent'],
                },
            ],
        }),
        [totalRegistrations]
    );

    const overlappingSegments: Plugin<'doughnut'> = {
        id: 'overlappingSegments',
        afterDatasetDraw(chart) {
            const { ctx, data } = chart;
            if (chart.getDatasetMeta(0).data[0]) {
                const x = chart.getDatasetMeta(0).data[0].x;
                const y = chart.getDatasetMeta(0).data[0].y;
                const angle = Math.PI / 180;
                const length = Number(data.labels?.length) - 1;

                chart.getDatasetMeta(0).data.forEach((_v, index) => {
                    if (index < length && chart.getDataVisibility(index) && data.datasets[0].data[index] > 0) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const metaData = chart.getDatasetMeta(0).data[index] as any; // Lack of documentation unfortunatelly
                        const innerRadius = metaData.innerRadius;
                        const outerRadius = metaData.outerRadius;
                        const endAngle = metaData.endAngle;
                        const radius = (outerRadius - innerRadius) / 2;
                        const coordinates = [];

                        for (let i = -0.1; i <= 0.1; i += 0.01) {
                            const xCoor = (innerRadius + radius) * Math.cos(endAngle + Math.PI + i);
                            const yCoor = (innerRadius + radius) * Math.sin(endAngle + i);
                            coordinates.push({ x: xCoor, y: yCoor });
                        }

                        ctx.save();
                        ctx.fillStyle = (data.datasets[0].backgroundColor as string[])[index];
                        ctx.translate(x, y);
                        ctx.beginPath();
                        coordinates.forEach(({ x, y }) => {
                            ctx.arc(-x, y, radius, 0, angle * 360, false);
                        });
                        ctx.fill();
                        ctx.restore();
                    }
                });
            }
        },
    };

    return (
        <>
            <Doughnut data={placeholderData} options={placeholderOptions} className="absolute" />
            {hasData ? (
                <Doughnut data={data} options={options} plugins={[overlappingSegments]} className="relative z-10" />
            ) : null}
        </>
    );
};

export { GuestRegistrationAnalytic };
